<template>
    <Head title="Update Review Post" />
    <BreezeAuthenticatedLayout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Update Review Post - {{ post.title }}
            </h2>
        </template>
        <BreadCrumbs :items="navItems"></BreadCrumbs>
        <div class="w-full my-6 overflow-hidden py-12 shadow">
            <div class="px-6 pt-4 pb-2">
                <form enctype="multipart/form-data" @submit.prevent="submit" class="w-full">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Title</admin-app-label>
                            <input-basic
                                placeholder="Title"
                                v-model="form.title"
                                :error="errors['title']"
                            />
                            <input-error v-if="errors['title']">
                                {{ errors.title }}
                            </input-error>
                        </div>
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Posted By</admin-app-label>
                            <input-select
                                placeholder="Posted By"
                                labelKey="Product"
                                v-model="form.created_by"
                                :options="users"
                                :error="errors['created_by']"
                                label="name"
                                :reduce="user => user.id"
                            >
                            </input-select>
                            <p class="text-sm text-placeholder">
                                Non compulsory field. Set this to show who created the post
                            </p>
                        </div>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Excerpt</admin-app-label>
                            <input-textarea
                                class="min-h-[200px]"
                                placeholder="Excerpt"
                                v-model="form.excerpt"
                                :error="errors['excerpt']"
                            />
                            <input-error v-if="errors['excerpt']">
                                {{ errors.excerpt }}
                            </input-error>
                            <p class="text-sm text-placeholder mt-1">
                                Add a small excerpt that will serve as a short description of this news post.
                                This excerpt will be used on the news landing page for a short description of the
                                article.
                            </p>
                        </div>
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Video ID</admin-app-label>
                            <input-basic
                                placeholder="Video ID"
                                v-model="form.video_id"
                                :error="errors[`video_id`]"
                            />
                            <p class="text-sm">e.g youtube.com/watch?v=<strong>5eZOOEkrf6Q</strong></p>
                        </div>
                    </div>
                    <div class="mb-6 md:mb-4">
                        <admin-app-label>Content</admin-app-label>
                        <input-wysiwyg
                            v-model="form.content"
                        ></input-wysiwyg>
                        <input-error v-if="errors['content']">
                            {{ errors.description }}
                        </input-error>
                    </div>
                    <div class="mb-6 md:mb-4">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <admin-app-label>CTA Text</admin-app-label>
                                <input-basic
                                    placeholder="CTA Text"
                                    v-model="form.cta_text"
                                    :error="errors['cta_text']"
                                />
                                <input-error v-if="errors['cta_text']">
                                    {{ errors.cta_text }}
                                </input-error>
                            </div>

                            <div>
                                <admin-app-label>CTA Link</admin-app-label>
                                <input-basic
                                    placeholder="CTA Link"
                                    v-model="form.cta_link"
                                    :error="errors['cta_link']"
                                />
                                <input-error v-if="errors['cta_link']">
                                    {{ errors.cta_link }}
                                </input-error>
                            </div>
                        </div>

                        <p class="text-sm text-placeholder mt-1">
                            Add a call to action button to the bottom of this article.
                            You can link to an external site, or another page within {{ config.name }}.
                        </p>
                    </div>

                    <div class="mb-6 md:mb-4">
                        <admin-app-label>Featured Brand</admin-app-label>
                        <input-select
                            placeholder="Featured Brand"
                            labelKey="Featured Brand"
                            v-model="form.featured_brand_id"
                            :options="brands"
                            :error="errors['featured_brand_id']"
                            label="name"
                            :reduce="brands => brands.id"
                        />
                        <p class="text-sm text-placeholder mt-1">Leave this blank to default to Bridgestone</p>
                    </div>

                    <seo-tags v-model="form.seo_tags" :errors="errors"></seo-tags>
                    <app-button type="submit" :disabled="form.processing" class="mt-8">Update</app-button>
                </form>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>

<script>
import {useForm} from "@inertiajs/inertia-vue3";
import AppButton from "@/Components/AppButton";
import AdminAppLabel from "@/Components/Admin/Forms/AdminAppLabel";
import BreezeAuthenticatedLayout from "@/Layouts/Admin/Authenticated.vue";
import {Head} from "@inertiajs/inertia-vue3";
import BreadCrumbs from "@/Components/Admin/BreadCrumbs";
import InputBasic from "@/Components/Admin/Forms/Inputs/InputBasic";
import InputError from "@/Components/Forms/Inputs/InputError";
import InputTextarea from "@/Components/Admin/Forms/Inputs/InputTextarea";
import InputSelect from "@/Components/Admin/Forms/Inputs/InputSelect";
import InputWysiwyg from "@/Components/Admin/Forms/Inputs/InputWysiwyg";
import SeoTags from "@/Components/Admin/Forms/SeoTags";

export default {
    name: "Edit",
    components: {
        AdminAppLabel,
        AppButton,
        BreadCrumbs,
        BreezeAuthenticatedLayout,
        Head,
        InputBasic,
        InputError,
        InputTextarea,
        InputSelect,
        InputWysiwyg,
        SeoTags
    },
    props: {
        post: Object,
        brands: Object,
        users: Object,
        config: Object,
        errors: Object
    },
    data() {
        return {
            navItems: [
                {title: "Review Posts", path: "/admin/review-posts"},
            ],
            form: this.$inertia.form({
                ...this.post,
                created_by: this.post.created_by.id ?? null,
                _method: "put",
            }),
        }
    },
    mounted() {
        this.navItems.push({
            title: this.post.slug,
            path: this.route("admin.review-posts.edit", this.post)
        });
    },
    methods: {
        submit()
        {
            this.form.post(`/admin/review-posts/${this.post.id}`);
        }
    }
}
</script>

<style scoped>

</style>
